import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  HostListener,
  SimpleChanges,
  ChangeDetectionStrategy,
  signal,
  computed,
  effect,
  input,
  output
} from '@angular/core';
import { CHAT_ASSET_BASE_URL } from '../../tokens/chat-asset-base-url.token';
import {
  IChatInfoAboutConfig,
  IChatInfoAttachmentEx,
  IChatInfoButtonConfigEx,
  IChatInfoActionConfig,
  IChatInfoAboutButtonsConfig,
  IChatNotificationSettingsConfig,
  MutedDirect
} from '../../interfaces/chat-info-interfaces';
import { ChatCloudLinkService } from '../../services/chat-cloudlink.service';
import { Attachment, Conversation, Participant } from '@mitel/cloudlink-sdk/chat';
import { from, Subject, of } from 'rxjs';
import { map, filter, takeUntil, catchError } from 'rxjs/operators';
import { AttachmentCollection, ParticipantCollection } from '@mitel/cloudlink-sdk/chat';
import { ChatInfoActions, ChatInfoButtons } from '../../enums/chat-info-buttons';
import { FileTypes } from '../../enums/file-types';
import { FileUtil } from '../../util/file.util';
import { ChatConversationService } from '../../services/chat-conversation.service';
import { ChatAuthService } from '../../services/chat-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ChatAdminService } from '../../services/chat-admin.service';
import { AttachmentDatePipe, UnreadMessageDatePipe } from '../../classes/chat-pipes';
import findIndex from 'lodash-es/findIndex';
import { MessageUtils } from '../../classes/messageutils';
import { ChatLoggingService } from '../../services/chat-logging.service';
import { SharedHelpers } from '../../classes/sharedhelpers';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChatConfirmationDialogComponent } from '../chat-confirmation-dialog/chat-confirmation-dialog.component';
import { AnalyticObject } from '../../interfaces/analytics-interfaces';
import { ChatAnalyticAttributes, ChatMaxValues, MuteOptions } from '../../classes/constants';
import { default as AnalyticEvents } from '@mitel-internal/cloudlink-analytic-events/analytic-events.json';
import { PrimeThemeService } from '../../themes/prime-theme.service';
import { MenuItem } from 'primeng/api';
import { MiMenuComponent } from '@mitel-internal/primeng-lib';

export interface ChatInfoConfig {
  showAttachmentOptionsDelete?: boolean;
  showAttachmentOptionsPreview?: boolean;
  showParticipantPointer?: boolean;
  addPeopleButton?: boolean;
  maxNotificationSettings?: number;
  activatePhoneClick?: boolean;
  activateEmailClick?: boolean;
  singleParticipantReason?: string;
  isOneAppSettingsError?: boolean;
  editStreamButton?: boolean;
  smsProviderID?: any;
  showDayOfWeek?: boolean;
  isOneToOneChat?: boolean;
  jsonMessagesAsSource?: boolean;
  titleHeight?: number;
  titleFontSize?: number;
}
// @ts-ignore
@Component({
  selector: 'mi-chat-info',
  templateUrl: './chat-info.component.html',
  styleUrls: ['./chat-info.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatInfoComponent implements OnInit, OnDestroy {
  buttonTypes: typeof ChatInfoButtons = ChatInfoButtons;

  private ngUnsubscribe: Subject<void> = new Subject();
  private setupUnsubscribe: Subject<void> = new Subject();

  // Signal-based internal state
  private _attachments = signal<IChatInfoAttachmentEx[]>([]);
  private _buttons = signal<IChatInfoButtonConfigEx[]>([]);
  public currentButtons = signal<IChatInfoButtonConfigEx[]>([]);
  private _conversationId = signal<string>('');
  private _notificationSettings = signal<IChatNotificationSettingsConfig>(null);
  public notificationsEnabled = computed(() => {
    const conv = this.conversation();
    const settings = this._notificationSettings();
    if (!settings || !conv) return false;

    if (conv.stream) {
      return settings.optedInStreams.indexOf(this._conversationId()) > -1;
    } else {
      const mutedDirect = settings.mutedDirects.find(md => md.conversationId === this._conversationId());
      return !mutedDirect;
    }
  });
  public alwaysMuted = computed(() => {
    const conv = this.conversation();
    const settings = this._notificationSettings();
    if (!settings || !conv || conv.stream) return false;

    const mutedDirect = settings.mutedDirects.find(md => md.conversationId === this._conversationId());
    return mutedDirect?.muteUntil === null;
  });
  private onlineTimeout = signal<any>(null);
  private attachmentNext = signal({ skip: 0, top: 40, skipToken: '' });
  private loadingAttachments = signal(false);
  private participantsNext = signal({ skip: 0, top: 40, skipToken: '' });
  private loadingParticipants = signal(false);
  private moreOptionsSelectedName = signal<string>(null);
  private moreOptionsSelectedElement = signal<any>(null);
  private attachmentHover = signal<string>(null);
  private attachmentHoverEffect = signal(false);

  public conversation = signal<Conversation>(null);
  public smsParticipant = signal<Participant>(null);
  public participantTotal = signal(0);
  public participantsList = signal<string[]>([]);
  public participantsLoaded = signal(false);
  public attachmentsLoaded = signal(false);
  public selectedIndex = signal<number | undefined>(undefined);
  public selectedConfig = signal<IChatInfoButtonConfigEx>(null);
  private settingsLoadErrorRefreshElem = signal<any>(null);
  public conversationLoaded = signal(false);
  private ref = signal<DynamicDialogRef | undefined>(undefined);

  dmMuteOptions = [MuteOptions.ONE_HOUR, MuteOptions.ONE_DAY, MuteOptions.ALWAYS];

  @ViewChild('infoContent') infoContent: ElementRef;
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  @ViewChild('oneAppSettingsError') set oneAppSettingsError(el: ElementRef) {
    if (el?.nativeElement) {
      const elem = el.nativeElement.getElementsByTagName('u')[0];
      this.settingsLoadErrorRefreshElem.set(elem);
      elem.style.setProperty('cursor', 'pointer');
      elem.removeEventListener('click', this.reloadSettings);
      elem.addEventListener('click', this.reloadSettings);
    }
  }

  public get attachments(): Attachment[] {
    const config = this.selectedConfig();
    if (config?.type === ChatInfoButtons.ALL_ATTACHMENTS || config?.type === ChatInfoButtons.IMAGES) {
      return config.type === ChatInfoButtons.ALL_ATTACHMENTS ? this._attachments().slice() : this.images;
    }
    return [];
  }

  private get images(): Attachment[] {
    return this._attachments().filter((file: Attachment) => {
      return FileUtil.getFileTypeFromName(file.name) === FileTypes.IMAGE;
    });
  }

  // Signal-based inputs with side effects
  buttons = input<IChatInfoButtonConfigEx[]>();
  conversationId = input<string>();
  notificationSettings = input<IChatNotificationSettingsConfig>();

  // Public getters for template access to internal signals
  get _notificationSettingsValue(): IChatNotificationSettingsConfig {
    return this._notificationSettings();
  }

  // Signal-based inputs
  title = input<string>('');
  about = input<IChatInfoAboutConfig[]>();
  aboutButtons = input<IChatInfoAboutButtonsConfig[]>();
  actions = input<IChatInfoActionConfig[]>();
  downloadAttachmentOverride = input<Function>();
  getParticipantsFromChatComponent = input<boolean>(false);
  titleAction = input<IChatInfoActionConfig>(null);
  hideCloseButton = input<boolean>(false);
  config = input<ChatInfoConfig>();

  // Signal-based outputs
  onClose = output<void>();
  onAction = output<string>();
  onInfoAboutButtonClick = output<string>();
  onParticipantClick = output<string>();
  onCallClick = output<string>();
  onEmailClick = output<string>();
  onPreferencesClick = output<void>();
  optInOrOutClick = output<void>();
  unmuteDMClick = output<void>();
  muteDMClick = output<MutedDirect>();
  reloadSettingsClick = output<void>();
  onEditStream = output<string>();
  onAnalyticEvent = output<AnalyticObject>();
  onGoToAttachmentMessage = output<string>();
  onFullScreenPreview = output<any>();

  @HostListener('document:click', ['$event']) onClick(event: MouseEvent | TouchEvent) {
    const selectedElem = this.moreOptionsSelectedElement();
    if (!selectedElem || !selectedElem.contains(event.target)) {
      this.closeMoreOptions();
    }
  }

  constructor(
    public el: ElementRef,
    private chatCloudlinkService: ChatCloudLinkService,
    public chatAuthService: ChatAuthService,
    private chatConversationService: ChatConversationService,
    public chatAdminService: ChatAdminService,
    public translate: TranslateService,
    private attachmentDatePipe: AttachmentDatePipe,
    private chatLoggingService: ChatLoggingService,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialogService: DialogService,
    private unreadMessageDatePipe: UnreadMessageDatePipe,
    public primeThemeService: PrimeThemeService,
    @Inject(CHAT_ASSET_BASE_URL) private assetBaseUrl: string
  ) {
    // Effect to watch buttons input and trigger setup
    effect(() => {
      const buttonsValue = this.buttons();
      if (!!buttonsValue) {
        this._buttons.set(buttonsValue);
        this.setupButtons();
      }
    });

    // Effect to watch conversationId input and trigger setup
    effect(() => {
      const conversationIdValue = this.conversationId();
      if (!!conversationIdValue) {
        this._conversationId.set(conversationIdValue);
        this.conversationLoaded.set(false);
        this.setupContent();
      }
    });

    // Effect to watch notificationSettings input and create deep copy
    effect(() => {
      const notificationSettingsValue = this.notificationSettings();
      if (!!notificationSettingsValue) {
        this._notificationSettings.set({
          ...notificationSettingsValue,
          mutedDirects: notificationSettingsValue.mutedDirects ? [...notificationSettingsValue.mutedDirects] : [],
          optedInStreams: notificationSettingsValue.optedInStreams ? [...notificationSettingsValue.optedInStreams] : []
        });
      }
    });
  }

  ngOnInit(): void {
    this.chatLoggingService.info({
      logMessage: 'chat-info.component.ngOnInit(): current language',
      logParameters: [this.translate.currentLang]
    });
    this.translateButtons();

    this.translate.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(event => {
      this.chatLoggingService.info({
        logMessage: 'chat-info.component.onLangChange(): current language',
        logParameters: [event.lang]
      });
      this.translateButtons();
    });

    // Avatars and names are read straight from chatAdminService, so refresh when a user's data changes
    this.chatAdminService.userChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.cdRef.markForCheck();
    });

    this.chatCloudlinkService.online$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(online => {
      const currentTimeout = this.onlineTimeout();
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      if (online) {
        let delay = 0;
        if (this.chatAuthService.offlineLogin) {
          //Wait for service to reset
          delay += 2500;
        }
        this.onlineTimeout.set(
          setTimeout(() => {
            this.chatLoggingService.info({
              logMessage: 'chat-info.component.ngOnInit(): Back online, setup content sync',
              logParameters: [this._conversationId()]
            });
            this.setupContent();
          }, delay)
        );
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.config) {
      if (this.config()?.showAttachmentOptionsDelete) {
        this.chatAuthService.getEditDeletePolicy();
      }
    }
    this.translateButtons();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    const currentTimeout = this.onlineTimeout();
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    const currentRef = this.ref();
    if (currentRef) {
      currentRef.close();
    }
    this.clearSettingsLoadErrorBinding();
  }

  public close(): void {
    this.onClose.emit();
  }

  getAttachmentDisplayName(attachment): string {
    return MessageUtils.getAttachmentDisplayName(attachment);
  }

  downloadAttachment(file: Attachment, event: MouseEvent | TouchEvent): void {
    if (this.downloadAttachmentOverride()) {
      this.downloadAttachmentOverride()({
        conversationId: this._conversationId(),
        attachmentId: file.name,
        name: this.getAttachmentDisplayName(file)
      });
    } else {
      this.chatConversationService.downloadAttachment({
        conversationId: this.conversation().conversationId,
        attachmentId: file.name,
        name: this.getAttachmentDisplayName(file)
      });
    }
  }

  onMouseEnter(file: Attachment) {
    this.attachmentHover.set(file.name);
    this.attachmentHoverEffect.set(true);
    this.enterZone();
  }

  onMouseLeave() {
    this.attachmentHover.set(null);
    this.attachmentHoverEffect.set(false);
  }

  mouseEnterOptionsArea(event) {
    this.attachmentHoverEffect.set(false);
    this.stopEvent(event);
  }

  mouseLeaveOptionsArea(event) {
    this.attachmentHoverEffect.set(true);
    this.stopEvent(event);
  }

  focusOutOptionsArea(event) {
    this.closeMoreOptions();
  }

  isAttachmentHoverEffect(file) {
    return this.attachmentHover() == file.name && this.attachmentHoverEffect();
  }

  closeMoreOptions() {
    this.moreOptionsSelectedName.set(null);
    this.moreOptionsSelectedElement.set(null);
  }

  showMoreAttachmentOptions(file: Attachment) {
    return file.createdBy === this.chatAuthService.userId && this.showDelete();
  }

  showMoreDropDown(file: Attachment, $event: Event) {
    const selectedName = this.moreOptionsSelectedName();
    if (selectedName && selectedName == file.name) {
      this.closeMoreOptions();
    } else {
      this.moreOptionsSelectedElement.set((<any>$event)?.currentTarget?.parentElement);
      this.moreOptionsSelectedName.set(file.name);
    }
    this.stopEvent($event);
  }

  isHovered(file) {
    return this.attachmentHover() === file.name;
  }

  moreOptionsSelected(file: Attachment) {
    return this.moreOptionsSelectedName() === file.name;
  }

  showDelete() {
    return this.config()?.showAttachmentOptionsDelete && this.chatAuthService.allowEditDelete;
  }

  deleteAttachment(file: Attachment, $event) {
    this.stopEvent($event);
    this.closeMoreOptions();
    const previouslyFocusedElement = document.activeElement as HTMLElement;
    const dialogRef = this.dialogService.open(ChatConfirmationDialogComponent, {
      data: {
        file: file,
        title: this.translate.instant('cl_chat.deleteDialogAttachmentButton'),
        bodyText: this.translate.instant('cl_chat.deleteDialogAttachmentMessage'),
        okButtonText: this.translate.instant('cl_chat.deleteDialogAttachmentButton'),
        cancelButtonText: this.translate.instant('cl_chat.deleteDialogCancelButton')
      },
      showHeader: false,
      modal: true,
      dismissableMask: true,
      focusOnShow: false,
      pt: { root: { role: 'alertdialog', 'aria-labelledby': 'chat-confirmation-dialog-title' } },
      styleClass: 'chat-confirmation-modal',
      contentStyle: { padding: '0', overflow: 'visible' }
    });
    this.ref.set(dialogRef);

    dialogRef.onClose.subscribe(async result => {
      if (!result) {
        previouslyFocusedElement?.focus();
      }
      this.chatLoggingService.info({
        logMessage: 'chat-info.component.deleteAttachment()',
        logParameters: [this._conversationId(), file.name]
      });
      if (result) {
        try {
          await this.chatConversationService.deleteAttachment({
            conversationId: this._conversationId(),
            attachmentId: file.name
          });
          MessageUtils.checkAndDeleteAttachmentMessage(
            file.name,
            this._conversationId(),
            this.chatCloudlinkService,
            this.chatAuthService,
            this.chatLoggingService,
            this.chatConversationService,
            this.translate
          );
        } catch (ex) {
          this.chatConversationService.errorDialog$.next({
            exceptionLogEvent: {
              logMessage: 'chat-info.component.deleteAttachment(): Error deleting attachment',
              logParameters: [ex]
            },
            errorMessage: this.translate.instant('cl_chat.errorDeletingAttachment')
          });
        }
      }
    });
  }

  stopEvent($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  public enterZone() {
    this.ngZone.run(() => {
      this.cdRef.markForCheck();
    });
  }

  private translateButtons() {
    this._buttons().forEach(button => {
      if (!button.titleTranslated) {
        switch (button.type) {
          case ChatInfoButtons.ALL_ATTACHMENTS:
            button.title = this.translate.instant('cl_chat.files');
            break;
          case ChatInfoButtons.IMAGES:
            button.title = this.translate.instant('cl_chat.images');
            break;
          case ChatInfoButtons.PARTICIPANTS:
            button.title = this.translate.instant('cl_chat.members');
            button.titleSingular = this.translate.instant('cl_chat.member');
            break;
          case ChatInfoButtons.NOTIFICATIONS:
            button.title = this.translate.instant('cl_chat.notifications');
            break;
        }
      }
    });
    this.cdRef.markForCheck();
  }

  private setupButtons(): void {
    this.setSelectedButton();

    this._buttons().forEach(button => {
      if (button.title) {
        // title handled by the hosted app
        button.titleTranslated = true;
      } else {
        button.titleTranslated = false;
      }
    });
  }

  private getAttachments(): void {
    if (!!this._conversationId() && !this.loadingAttachments()) {
      this.loadingAttachments.set(true);
      this.chatAuthService.ready$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
        (ready: boolean) => {
          if (ready) {
            const currentNext = this.attachmentNext();
            from(
              this.chatCloudlinkService.chat.getAttachments({
                conversationId: this._conversationId(),
                skip: currentNext.skip,
                skipToken: currentNext.skipToken,
                top: currentNext.top,
                orderby: 'createdOn desc'
              })
            )
              .pipe(
                catchError(ex => {
                  this.chatLoggingService.error({
                    logMessage: 'chat-info.component.getAttachments(): Error',
                    logParameters: [ex]
                  });
                  return of(null);
                }),
                map(res => {
                  let collection: AttachmentCollection = res.body;
                  if (collection?._links) {
                    var next = collection._links.next;
                    if (next) {
                      const queryString: any = SharedHelpers.parseQueryString(next);
                      this.attachmentNext.set({
                        skip: queryString.$skip,
                        skipToken: queryString.$skipToken,
                        top: queryString.$top
                      });
                    } else {
                      this.attachmentNext.set({
                        skip: 0,
                        skipToken: '',
                        top: 50
                      });
                    }
                  }

                  if (collection?._embedded?.items) {
                    return collection._embedded.items;
                  }
                  return [];
                }),
                takeUntil(this.setupUnsubscribe),
                takeUntil(this.ngUnsubscribe)
              )
              .subscribe(async (attachments: Attachment[]) => {
                await this.chatAdminService.getPrincipals(attachments.map(a => a.createdBy));
                attachments = attachments.map(a => {
                  return this.addClassDate(a);
                });
                const currentAttachments = [...this._attachments()];
                attachments.forEach(a => {
                  const i: number = findIndex(
                    currentAttachments,
                    (attachment: Attachment) => attachment.name === a.name
                  );
                  if (i >= 0) {
                    Object.assign(currentAttachments[i], a);
                  } else {
                    //Add to the working array
                    currentAttachments.push(a);
                  }
                });
                //Sort and update signal once with all changes
                currentAttachments.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
                this._attachments.set(currentAttachments);
                this.chatLoggingService.info({
                  logMessage: 'chat-info.component.getAttachments(): Got attachments',
                  logParameters: [this._conversationId()]
                });
                this.loadingAttachments.set(false);
                this.attachmentsLoaded.set(true);
              });
          }
        },
        er => {
          //Let chat component handle error
        }
      );
    }
  }

  private addClassDate(a: Attachment) {
    var attachment = a as IChatInfoAttachmentEx;
    this.chatAdminService.getPrincipal(a.createdBy);
    attachment.dateString = this.attachmentDatePipe.transform(attachment.createdOn, this.translate.currentLang);
    return a;
  }

  private async getConversation() {
    return new Promise<void>(resolve => {
      if (!!this._conversationId() && this.chatCloudlinkService.chat) {
        this.chatAuthService.ready$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
          async () => {
            from(
              this.chatCloudlinkService.chat.getConversation({
                conversationId: this._conversationId()
              })
            )
              .pipe(
                catchError(ex => {
                  this.chatLoggingService.error({
                    logMessage: 'chat-info.component.getConversation(): Error',
                    logParameters: [ex]
                  });
                  return of(null);
                }),
                takeUntil(this.ngUnsubscribe)
              )
              .subscribe(async res => {
                const conversation: Conversation = res.body;
                if (conversation) {
                  if (conversation?._embedded?.participants?.total) {
                    this.participantTotal.set(conversation?._embedded?.participants?.total);
                  }
                  this.conversation.set(conversation);
                  this.chatLoggingService.info({
                    logMessage: 'chat-info.component.getConversation(): Got conversation',
                    logParameters: [conversation.conversationId]
                  });
                  this.conversationLoaded.set(true);
                  try {
                    //Need to wait to get conversation so we can use the embedded list of the conversation
                    await this.getParticipants();
                  } catch (ex) {
                    this.chatLoggingService.error({
                      logMessage: 'chat-info.component.getConversation(): Error getting participants',
                      logParameters: [ex]
                    });
                  }
                }
                resolve();
              });
          },
          er => {
            //Let chat component handle error
          }
        );
      } else {
        resolve();
      }
    });
  }

  getFileType(filename: string): FileTypes {
    return FileUtil.getFileTypeFromName(filename);
  }

  getFileSize(file: Attachment): string {
    return SharedHelpers.getFileSize(file.length);
  }

  private async getParticipants() {
    if (this.getParticipantsFromChatComponent()) {
      let firstAttachmentUpdate = true;
      this.chatConversationService.participantsUpdated$
        .pipe(takeUntil(this.setupUnsubscribe), takeUntil(this.ngUnsubscribe))
        .subscribe(c => {
          if (c.conversationId === this._conversationId()) {
            if (c.participants) {
              if (this.config()?.smsProviderID) {
                let newArray: any[] = c.participants.filter(
                  (item: any) => !(item.userId == this.config()?.smsProviderID)
                );
                this.participantTotal.set(newArray.length);
                this.participantsList.set(newArray.map(p => p.userId));
              } else {
                this.participantTotal.set(c.participants.length);
                this.participantsList.set(c.participants.map(p => p.userId));
              }
              const foundSmsParticipant = c.participants.find(p => p.participantType === 'SMS');
              this.smsParticipant.set(foundSmsParticipant);
              if (foundSmsParticipant) {
                this.currentButtons.set(this._buttons().filter(b => b.type !== ChatInfoButtons.ALL_ATTACHMENTS));
              } else {
                this.currentButtons.set(this._buttons());
              }
              this.sortParticipants();
              this.cdRef.markForCheck();

              this.participantsLoaded.set(true);
              //Only want to setup attachments after we get the first attachment update
              if (firstAttachmentUpdate) {
                firstAttachmentUpdate = false;
                this.setupAttachments();
              }
            } else if (c.largeLoad) {
              this.checkParticipants();
              this.listenForParticipants();
            }
          }
        });
      this.chatConversationService.chatInfoSetupContent$
        .pipe(takeUntil(this.setupUnsubscribe), takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.chatLoggingService.info({
            logMessage: 'chat-info.component.getParticipants(): User was set back to ACCEPTED, reloading'
          });
          this.setupContent();
        });
    } else {
      await this.checkParticipants();
    }
  }

  private async checkParticipants() {
    const conv = this.conversation();
    if (conv?._embedded?.participants?._embedded?.items?.length > 0) {
      this.participantsList.set(conv._embedded.participants._embedded.items.map(user => user.userId));
      let next = conv._embedded.participants._links.next;
      if (next) {
        this.participantsList.set([]);
        this.loadParticipants();
      } else {
        //Need to wait for chat admin service to get the participants before loading attachments so the names display properly
        this.setupAttachments();
        this.participantsLoaded.set(true);
      }
    }
  }

  private loadParticipants() {
    if (!!this._conversationId() && !this.loadingParticipants()) {
      this.loadingParticipants.set(true);

      let firstLoad = false;
      const currentParticipantsList = this.participantsList();
      if (currentParticipantsList.length === 0) {
        firstLoad = true;
      }

      const currentNext = this.participantsNext();
      from(
        this.chatCloudlinkService.chat.getParticipants({
          conversationId: this._conversationId(),
          skip: currentNext.skip,
          skipToken: currentNext.skipToken,
          top: currentNext.top
        })
      )
        .pipe(
          catchError(ex => {
            this.chatLoggingService.error({
              logMessage: 'chat-info.component.loadParticipants(): Error',
              logParameters: [ex]
            });
            return of(null);
          }),
          map(res => {
            let collection: ParticipantCollection = res.body;
            if (collection?._links) {
              var next = collection._links.next;
              if (next) {
                const queryString: any = SharedHelpers.parseQueryString(next);
                this.participantsNext.set({
                  skip: queryString.$skip,
                  skipToken: queryString.$skipToken,
                  top: queryString.$top
                });
              } else {
                this.participantsNext.set({
                  skip: 0,
                  skipToken: '',
                  top: 50
                });
              }
            }

            if (collection?._embedded?.items) {
              return collection._embedded.items;
            }
            return [];
          }),
          takeUntil(this.setupUnsubscribe),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(async (participants: Participant[]) => {
          await this.chatAdminService.getPrincipals(participants.map(p => p.userId));
          let newParticipants = participants.map(p => p.userId);
          const currentList = this.participantsList();
          newParticipants = newParticipants.filter(p => currentList.indexOf(p) === -1);
          this.participantsList.set([...currentList, ...newParticipants]);
          this.chatLoggingService.info({
            logMessage: 'chat-info.component.loadParticipants(): Got participants',
            logParameters: [this._conversationId()]
          });
          this.loadingParticipants.set(false);

          if (firstLoad) {
            //Need to wait for chat admin service to get the participants before loading attachments so the names display properly
            this.setupAttachments();
            this.participantsLoaded.set(true);
          }
        });
    }
  }

  private listenForAttachments(): void {
    this.chatConversationService.attachmentManualNotify$
      .pipe(
        filter(attachment => (<any>attachment).conversationId === this._conversationId()),
        takeUntil(this.setupUnsubscribe),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((attachment: Attachment) => {
        this.handleAttachment(attachment);
      });
    this.chatCloudlinkService.attachmentsChanged$
      .pipe(
        filter(attachment => (<any>attachment).conversationId === this._conversationId()),
        takeUntil(this.setupUnsubscribe),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((attachment: Attachment) => {
        this.handleAttachment(attachment);
      });
    this.chatCloudlinkService.attachmentsDeleted$
      .pipe(
        filter(attachment => this._attachments().find(a => a.name === attachment) !== undefined),
        takeUntil(this.setupUnsubscribe),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(async deletedAttachment => {
        const currentAttachments = this._attachments();
        let matchingAttachment = currentAttachments.find(p => p.name === deletedAttachment);
        if (matchingAttachment) {
          this.chatLoggingService.info({
            logMessage: 'chat-info.component.listenForAttachments(): Attachment was removed',
            logParameters: [this._conversationId(), matchingAttachment]
          });
          //Need to assign a changed array to trigger CD on cdkVirtualFor
          this._attachments.set(currentAttachments.filter(p => p !== matchingAttachment));
        }
      });
  }

  private handleAttachment(attachment: Attachment) {
    try {
      if (attachment.displayName) {
        this.chatLoggingService.info({
          logMessage: 'chat-info.component.handleAttachment(): Attachment Received',
          logParameters: [attachment.displayName]
        });
        attachment = this.addClassDate(attachment);
        const currentAttachments = [...this._attachments()];
        const i: number = findIndex(currentAttachments, (a: Attachment) => a.name === attachment.name);
        if (i >= 0) {
          Object.assign(currentAttachments[i], attachment);
        } else {
          //Add new attachment to the working array
          currentAttachments.push(attachment);
        }
        //Sort and update signal once with all changes
        currentAttachments.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
        this._attachments.set(currentAttachments);
      }
    } catch (ex) {
      // stifle exception to prevent unsubscribing
      this.chatLoggingService.error({
        logMessage: 'chat-info.component.handleAttachment(): Error in subscribe',
        logParameters: [ex]
      });
    }
  }

  private listenForParticipants(): void {
    this.chatCloudlinkService.participantsChanged$
      .pipe(
        filter(p => p.conversationId === this._conversationId()),
        takeUntil(this.setupUnsubscribe),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(participant => {
        if (participant.method === 'POST') {
          this.chatLoggingService.info({
            logMessage: 'chat-info.component.listenForParticipants(): User was added',
            logParameters: [this._conversationId(), participant.userId]
          });
          this.participantTotal.update(total => total + 1);
          const currentList = this.participantsList();
          const i: number = findIndex(currentList, p => p === participant.userId);
          if (i < 0) {
            if (participant.status === 'ACCEPTED' && participant.userId === this.chatAuthService.userId) {
              this.chatLoggingService.info({
                logMessage: 'chat-info.component.listenForParticipants(): User was set back to ACCEPTED, reloading'
              });
              this.setupContent();
            } else {
              this.chatAdminService.getPrincipal(participant.userId);
              //Need to assign a changed array to trigger CD on cdkVirtualFor
              this.participantsList.set([participant.userId, ...currentList]);
            }
          }
        }
      });
    this.chatCloudlinkService.participantsDeleted$
      .pipe(
        filter(p => p.conversationId === this._conversationId()),
        takeUntil(this.setupUnsubscribe),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(async deletedParticipant => {
        if (deletedParticipant.conversationId === this._conversationId()) {
          this.participantTotal.update(total => total - 1);
          const currentList = this.participantsList();
          var matchingParticipant = currentList.find(p => p === deletedParticipant.userId);
          if (matchingParticipant) {
            this.chatLoggingService.info({
              logMessage: 'chat-info.component.listenForParticipants(): User was removed',
              logParameters: [this._conversationId(), matchingParticipant]
            });
            //Need to assign a changed array to trigger CD on cdkVirtualFor
            this.participantsList.set(currentList.filter(p => p !== matchingParticipant));
          }
        }
      });
  }

  private needsAttachments(): boolean {
    if (this.smsParticipant()) {
      return false;
    }
    return (
      this._buttons().filter((button: IChatInfoButtonConfigEx) => {
        return button.type === ChatInfoButtons.ALL_ATTACHMENTS || button.type === ChatInfoButtons.IMAGES;
      }).length > 0
    );
  }

  private setupContent(): void {
    if (this.setupUnsubscribe) {
      this.setupUnsubscribe.next();
      this.setupUnsubscribe.complete();
      this.setupUnsubscribe = new Subject();
    }
    this.participantTotal.set(0);
    this.participantsList.set([]);
    this.participantsLoaded.set(false);
    this.loadingParticipants.set(false);
    this.participantsNext.set({ skip: 0, top: 40, skipToken: '' });
    this._attachments.set([]);
    this.attachmentNext.set({ skip: 0, top: 40, skipToken: '' });
    this.loadingAttachments.set(false);
    this.attachmentsLoaded.set(false);
    this.chatAuthService.ready$.pipe(takeUntil(this.setupUnsubscribe), takeUntil(this.ngUnsubscribe)).subscribe(
      async (ready: boolean) => {
        if (!this.chatCloudlinkService.isOffline()) {
          if (this.config()?.jsonMessagesAsSource) {
            await this.getParticipants();
          } else {
            await this.getConversation();
          }
        }
        if (!this.getParticipantsFromChatComponent()) {
          this.listenForParticipants();
        }
      },
      er => {
        //Let chat component handle error
      }
    );
  }

  private setupAttachments(): void {
    if (this.needsAttachments()) {
      this.getAttachments();
      this.listenForAttachments();
    }
  }

  public selectButton(index: number): void {
    if (index !== this.selectedIndex()) {
      this.selectedIndex.set(index);
      this.setSelectedButton();
    }
  }

  public setSelectedButton(): void {
    const currentIndex = this.selectedIndex();
    this.selectedConfig.set(!!this._buttons()[currentIndex] ? this._buttons()[currentIndex] : undefined);
    const config = this.selectedConfig();
    if (config?.type === this.buttonTypes.ALL_ATTACHMENTS) {
      let analyticObj: AnalyticObject = {
        eventName: AnalyticEvents.analytic_events.common.event_name.chat.browse_files,
        attributes: ChatAnalyticAttributes
      };
      this.onAnalyticEvent.emit(analyticObj);
    }
    this.scrollToContent();
  }

  private scrollToContent() {
    try {
      if (this.infoContent) {
        this.infoContent.nativeElement.scrollTop = this.infoContent.nativeElement.scrollHeight;
      }
    } catch (err) {
      this.chatLoggingService.error({
        logMessage: 'chat-info.component.scrollToContent(): Error while scrolling',
        logParameters: [err]
      });
    }
  }

  public actionClick(key: string): void {
    let analyticObj: AnalyticObject;
    switch (key) {
      case ChatInfoActions.HIDE:
        analyticObj = {
          eventName: AnalyticEvents.analytic_events.common.event_name.chat.hide_direct_message,
          attributes: ChatAnalyticAttributes
        };
        this.onAnalyticEvent.emit(analyticObj);
        break;
      case ChatInfoActions.LEAVE:
        analyticObj = {
          eventName: AnalyticEvents.analytic_events.common.event_name.chat.leave_stream,
          attributes: ChatAnalyticAttributes
        };
        this.onAnalyticEvent.emit(analyticObj);
        break;
    }
    this.onAction.emit(key);
  }

  public infoAboutButtonClick(key: string): void {
    this.onInfoAboutButtonClick.emit(key);
  }

  public trackByFn(index, item) {
    return index;
  }

  public attachmentScrolledIndexChanged(event) {
    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();
    if (Math.abs(total - end) < 5) {
      const currentNext = this.attachmentNext();
      if (currentNext.skipToken) {
        this.getAttachments();
      }
    }
  }

  public participantScrolledIndexChanged(event) {
    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();
    if (Math.abs(total - end) < 5) {
      const currentNext = this.participantsNext();
      if (currentNext.skipToken) {
        this.loadParticipants();
      }
    }
  }

  private sortParticipants() {
    const currentList = this.participantsList();
    if (currentList.length && currentList.length > 1) {
      const sortedList = [...currentList].sort((a, b) => {
        if (this.chatAdminService.displayNames[a] == null && this.chatAdminService.displayNames[b] == null) {
          return 0;
        }
        if (this.chatAdminService.displayNames[a] == null) {
          return 1;
        }
        if (this.chatAdminService.displayNames[b] == null) {
          return -1;
        }
        if (this.chatAdminService.displayNames[a].toLowerCase() < this.chatAdminService.displayNames[b].toLowerCase()) {
          return -1;
        }
        if (this.chatAdminService.displayNames[a].toLowerCase() > this.chatAdminService.displayNames[b].toLowerCase()) {
          return 1;
        }
        return 0;
      });
      this.participantsList.set(sortedList);
    }
  }

  public participantClicked(participant) {
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.other.user_view_profile,
      attributes: ChatAnalyticAttributes
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.onParticipantClick.emit(participant);
  }

  public callClick(number: string) {
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.voice.make_call,
      attributes: {
        ...ChatAnalyticAttributes,
        AppFeature: AnalyticEvents.attributes.common.app_feature.voice,
        VoiceDestination: AnalyticEvents.attributes.common.voice_destination.internal
      }
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.onCallClick.emit(number);
  }

  public emailClick(email: string) {
    this.onEmailClick.emit(email);
  }

  public preferencesClick() {
    this.onPreferencesClick.emit();
  }

  public getInfoButtonMembersText(button: IChatInfoButtonConfigEx) {
    const total = this.participantTotal();
    let memberString = total + ' ' + button.title;
    if (total === 1 && button.titleSingular) {
      memberString = total + ' ' + button.titleSingular;
    }
    if (!this.translate.currentLang || this.translate.currentLang.toLowerCase().indexOf('de') === -1) {
      memberString = memberString.toLowerCase();
    }
    return memberString;
  }

  public getNotificationsStatus() {
    const conv = this.conversation();
    if (conv?.stream) {
      return this.notificationsEnabled()
        ? this.translate.instant('cl_chat.areEnabled', {
            settings: this.translate.instant('cl_chat.notifications')
          })
        : this.translate.instant('cl_chat.notEnabled', {
            settings: this.translate.instant('cl_chat.notifications')
          });
    } else if (!conv?.stream) {
      return this.notificationsEnabled()
        ? this.translate.instant('cl_chat.areEnabled', {
            settings: this.translate.instant('cl_chat.notifications')
          })
        : this.translate.instant('cl_chat.areMuted', {
            settings: this.translate.instant('cl_chat.notifications')
          });
    }
  }

  public getNotificationsNote() {
    const conv = this.conversation();
    const settings = this._notificationSettings();
    let numberLeft = conv?.stream
      ? this.config()?.maxNotificationSettings - settings.optedInStreams.length
      : this.config()?.maxNotificationSettings - settings.mutedDirects.length;
    let verb = (
      conv?.stream ? this.translate.instant('cl_chat.optInTo') : this.translate.instant('cl_chat.mute')
    ).toLowerCase();
    let object;
    if (numberLeft === 0) {
      return conv?.stream
        ? this.translate.instant('cl_chat.optedInLimit', { number: this.config()?.maxNotificationSettings })
        : this.translate.instant('cl_chat.mutedLimit', { number: this.config()?.maxNotificationSettings });
    } else {
      if (conv?.stream) {
        object = `${
          numberLeft != 1 ? this.translate.instant('cl_chat.streams') : this.translate.instant('cl_chat.stream')
        }`;
      } else if (!conv?.stream) {
        object = `${(numberLeft != 1
          ? this.translate.instant('cl_chat.conversations')
          : this.translate.instant('cl_chat.conversation')
        ).toLowerCase()}`;
      }
      return `${this.translate.instant('cl_chat.youCanOnly', { verb: verb, number: numberLeft, object: object })}.`;
    }
  }

  public getWorkingOn() {
    const conv = this.conversation();
    return `${this.translate.instant('cl_chat.workingOnUpdates', {
      verb: (conv?.stream
        ? this.translate.instant('cl_chat.optInTo')
        : this.translate.instant('cl_chat.mute')
      ).toLowerCase(),
      object: conv?.stream
        ? this.translate.instant('cl_chat.streams')
        : this.translate.instant('cl_chat.conversations').toLowerCase()
    })}`;
  }

  public optInOrOut() {
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.toggle_notifications,
      attributes: {
        ...ChatAnalyticAttributes,
        Status: this.notificationsEnabled()
          ? AnalyticEvents.attributes.common.status.disable
          : AnalyticEvents.attributes.common.status.enable
      }
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.optInOrOutClick.emit();
  }

  public unmuteDM() {
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.toggle_notifications,
      attributes: { ...ChatAnalyticAttributes, Status: AnalyticEvents.attributes.common.status.enable }
    };
    this.onAnalyticEvent.emit(analyticObj);
    this.unmuteDMClick.emit();
  }

  public muteDMWithOption($event) {
    let analyticObj: AnalyticObject = {
      eventName: AnalyticEvents.analytic_events.common.event_name.chat.toggle_notifications,
      attributes: { ...ChatAnalyticAttributes, Status: AnalyticEvents.attributes.common.status.disable }
    };
    this.onAnalyticEvent.emit(analyticObj);
    let muteUntil = null;
    switch ($event) {
      case MuteOptions.ONE_HOUR:
        muteUntil = new Date(Date.now() + 60 * 60 * 1000);
        break;
      case MuteOptions.ONE_DAY:
        muteUntil = new Date(Date.now() + 24 * (60 * 60 * 1000));
        break;
      case MuteOptions.ALWAYS:
        muteUntil = null;
    }
    let mutedDirect: MutedDirect = { conversationId: this._conversationId(), muteUntil: muteUntil };
    this.muteDMClick.emit(mutedDirect);
  }

  getMuteDropdownPrefix(): string {
    let prefix = '';
    if (this.notificationsEnabled()) {
      prefix = this.translate.instant('cl_chat.mute');
    } else {
      const settings = this._notificationSettings();
      let mutedDirect: MutedDirect = settings.mutedDirects.find(
        mutedDirect => mutedDirect.conversationId === this._conversationId()
      );
      let muteUntil = this.unreadMessageDatePipe.transform(mutedDirect?.muteUntil, this.translate.currentLang);
      prefix = this.translate.instant('cl_chat.mutedUntil', { time: muteUntil });
    }
    return prefix;
  }

  getMuteMenuItems(): MenuItem[] {
    return this.dmMuteOptions.map(option => ({
      label: this.translate.instant(option),
      // Colors only the resting label red (matching the button's own danger styling); hover/focus
      // intentionally fall back to mi-menu's default item color, since red text against mi-menu's
      // surface-tinted hover/focus background fails 4.5:1 in light mode (same math as CL-28956/Story 23).
      styleClass: '[--p-menu-item-color:var(--p-text-danger)]',
      command: () => this.muteDMWithOption(option)
    }));
  }

  // mi-menu portals its popup onto document.body (outside this component's DOM/style scope), and
  // has no width input yet, so it shrink-to-fits its content instead of matching the trigger button.
  // Widen the freshly-opened instance to match here rather than in the shared library.
  onMuteMenuToggle(trigger: HTMLElement, menu: MiMenuComponent): void {
    if (!menu.isOpen) {
      return;
    }
    const menuEls = document.querySelectorAll<HTMLElement>('.mi-menu');
    const menuEl = menuEls[menuEls.length - 1];
    if (menuEl) {
      menuEl.style.width = `${trigger.offsetWidth}px`;
    }
  }

  public isDirect(): boolean {
    const conv = this.conversation();
    return this.participantTotal() <= 2 && !conv?.stream && !this.config()?.isOneToOneChat;
  }

  public isGroupDirect(): boolean {
    const conv = this.conversation();
    return this.participantTotal() > 2 && !conv?.stream;
  }

  public isStream(): boolean {
    const conv = this.conversation();
    return conv?.stream;
  }

  public isOneToOneChat(): boolean {
    return this.config()?.isOneToOneChat;
  }

  public imageSource(p) {
    if (SharedHelpers.validateE164Num(p)) {
      if (this.config()?.isOneToOneChat) {
        return SharedHelpers.resolveAssetUrl(this.assetBaseUrl, 'assets/images/cl-chat/one-one-sms.svg');
      } else {
        return SharedHelpers.resolveAssetUrl(this.assetBaseUrl, 'assets/images/cl-chat/textGroup-external.svg');
      }
    } else if (this.chatAdminService.avatars[p]?.hasOwnProperty('50')) {
      return this.chatAdminService.avatars[p][50];
    } else {
      return '';
    }
  }

  private clearSettingsLoadErrorBinding() {
    const elem = this.settingsLoadErrorRefreshElem();
    if (elem) {
      elem.removeEventListener('click', this.reloadSettings);
      this.settingsLoadErrorRefreshElem.set(null);
    }
  }

  reloadSettings = (): void => {
    this.reloadSettingsClick.emit();
  };

  editStream() {
    this.onEditStream.emit(this._conversationId());
  }

  goToAttachmentMessage(file: Attachment) {
    this.onGoToAttachmentMessage.emit(file.name);
  }

  onAttachmentRowKeydown(event: Event, file: Attachment): void {
    // The row and its nested download/preview/more/delete controls are all focusable, and keydown
    // bubbles from them up to this handler regardless of their own click/keydown handling - only
    // react when the row itself (not a nested control) is the actual keydown target.
    if (event.target !== event.currentTarget) {
      return;
    }
    if ((event as KeyboardEvent).code === 'Space') {
      event.preventDefault();
    }
    this.goToAttachmentMessage(file);
  }

  fullScreenPreview(file: Attachment) {
    this.onFullScreenPreview.emit({ attachmentId: file.name, type: this.getFileType(file.name) });
  }

  showPreviewMessageOptions(file: Attachment): boolean {
    let isLargeImage = false;
    if (this.getFileType(file?.name) === FileTypes.IMAGE) {
      let fileSizeString = this.getFileSize(file);
      let imageSize = SharedHelpers.getBytesSize(fileSizeString);
      isLargeImage = Number(imageSize) > ChatMaxValues.MaxImageSize;
    }
    return (
      ((this.getFileType(file?.name) === FileTypes.IMAGE && !isLargeImage) ||
        this.getFileType(file?.name) === FileTypes.PDF) &&
      this.config()?.showAttachmentOptionsPreview
    );
  }
}
